import { useQuery } from '@tanstack/react-query'
import { TriangleAlert } from 'lucide-react'

export type ClosedToday = {
  closed: boolean
  /** The studio's own words for it, or null when it is open. */
  sentence: string | null
}

async function fetchClosedToday(): Promise<ClosedToday> {
  const res = await fetch('/api/closed-today')
  if (!res.ok) throw new Error(`Could not ask whether the studio is open (${res.status})`)
  return res.json()
}

/**
 * *The studio is shut today*, across the top of every public page (D-736).
 *
 * **Closing the studio told everybody except the website.** The worker emails
 * and pushes, the calendar empties, the registers go — and the public site
 * carried on as though nothing had happened. A snow day at 6am is exactly when
 * somebody checks the website rather than her inbox.
 *
 * **Derived, so it clears itself.** It reads the closure rather than a content
 * block, which means nothing of Tonya's is overwritten and there is nothing to
 * tidy up when the closure lifts or the day passes.
 *
 * **Silent on failure, deliberately.** Everything else on this page is correct
 * whether or not this answers, and a red bar reading *could not check* on the
 * public homepage is worse than no bar — it reads as *something is wrong with
 * the studio* to somebody who came to look at class times.
 */
export default function ClosedTodayBanner() {
  const { data } = useQuery({
    queryKey: ['closed-today'],
    queryFn: fetchClosedToday,
    // She closes the studio and goes to look. A stale answer here is the one
    // thing this banner must not serve.
    staleTime: 60_000,
    refetchOnWindowFocus: 'always',
    retry: 1,
  })

  if (!data?.closed || !data.sentence) return null

  return (
    <div role="status" className="border-b border-crit bg-crit-bg">
      <div className="mx-auto flex max-w-[56rem] flex-wrap items-center gap-x-3 gap-y-1 px-5 py-2.5">
        <TriangleAlert size={16} className="shrink-0 text-crit" aria-hidden="true" />
        <p className="m-0 min-w-0 text-[14px] font-semibold text-ink">{data.sentence}</p>
      </div>
    </div>
  )
}
